import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import MapView from './components/MapView.jsx';
import Sidebar from './components/Sidebar.jsx';
import ModeSwitcher from './components/ModeSwitcher.jsx';
import TripPlanner from './components/TripPlanner.jsx';
import DetailSkeleton from './components/DetailSkeleton.jsx';
import { WarnIcon } from './components/icons.jsx';

// Selection panels are lazy-loaded: they (plus the edit forms and the baked
// Einzellagen mappings they import) stay out of the initial bundle and only
// load when something is selected. DetailSkeleton doubles as the Suspense
// fallback, so the loading look is identical either way.
const DetailPanel = lazy(() => import('./components/DetailPanel.jsx'));

// Baked-in georeference for public/franken-lagen.png: least-squares fit of
// vineyard-cluster control points (~12.87 m/px ground scale), micro-refined
// by correlating the app's Franken polygons against the map's colored Lagen.
const DEFAULT_LAGEN_BOUNDS = [[49.290542, 8.943496], [50.249779, 11.043314]];
const BulkEditPanel = lazy(() => import('./components/BulkEditPanel.jsx'));
const DrawPanel = lazy(() => import('./components/DrawPanel.jsx'));
const FlurbPanel = lazy(() => import('./components/FlurbPanel.jsx'));
const RlpLagenPanel = lazy(() => import('./components/RlpLagenPanel.jsx'));
const FlyThrough = lazy(() => import('./components/FlyThrough.jsx'));
const VillagePanel = lazy(() => import('./components/VillagePanel.jsx'));
const GeoFeaturePanel = lazy(() => import('./components/GeoFeaturePanel.jsx'));
const ProducerPanel = lazy(() => import('./components/ProducerPanel.jsx'));
const StudyMode = lazy(() => import('./components/StudyMode.jsx'));
import { useAuth } from './hooks/useAuth.js';
import { bereicheFor } from './data/regionsAndBereiche.js';
import { ATLAS } from './data/atlasConfig.js';
import { useMapData } from './hooks/useMapData.js';
import { useOutOfScopeVineyards } from './hooks/useOutOfScopeVineyards.js';
import { supabase } from './lib/supabase.js';
import { ringSelfIntersects } from './utils/drawGeometry.js';
import { isKeyVineyard } from './utils/drillStack.js';
import { keepsToggle } from './utils/toggleReset.js';
import { matchesSite } from './utils/siteMatch.js';
import { cutAddPoint, cutUndo, cutFinishLine, cutEffectiveLines, cutLinesToGeoJSON } from './utils/splitCuts.js';
import { useKeyItems } from './hooks/useKeyItems.js';
import { useDrawVineyard } from './hooks/useDrawVineyard.js';
import { useFlurbereinigung } from './hooks/useFlurbereinigung.js';
import { isMobile, isMobileLayout } from './utils/isMobile.js';

// Selection shape:
//   { type: 'none' }
//   { type: 'single', id }
//   { type: 'multi',  ids: [] }
//   { type: 'village', village }
//   { type: 'producer', producer }

// Brand theme is permanent — always apply before React renders
document.documentElement.classList.add('theme-brand');

// Slim sidebar panel for the custom-vineyard geometry editor: the map does
// the work, this holds the instructions, validity state and Save/Cancel.
function GeometryEditPanel({ session, allValid, saving, error, onCancel, onSave, onUndo }) {
  const canUndo = (session.history?.length ?? 0) > 0;
  const isOsm = session.osmType !== 'custom';
  return (
    <aside className="detail">
      <header className="detail__header">
        <button className="detail__back" onClick={onCancel}>×</button>
        <div className="detail__title-wrap">
          <h2 className="detail__title">Edit Geometry</h2>
          <p className="detail__sub">{session.name}</p>
        </div>
      </header>
      <div className="detail__body">
        <div className="detail__notice">
          Drag the points on the map to reshape the polygon; the dashed +
          handles add a point mid-segment. Hold Shift and drag to lasso
          several points, then press Delete to remove them — the ring
          recloses without them. Only the shape changes — every other field
          stays exactly as it is.
        </div>
        {isOsm && (
          <div className="detail__notice" style={{ margin: '0 1.65rem 0 1.485rem' }}>
            This is OSM source data ({session.osmRef}). Saving keeps the OSM
            polygon untouched: your shape becomes a new custom vineyard with
            all fields copied over, and the OSM original is marked out of
            scope.
          </div>
        )}
        <button className="btn btn--ghost geom-edit__undo" onClick={onUndo} disabled={!canUndo || saving}>
          ↩ Undo{canUndo ? '' : ' (nothing to undo)'}
        </button>
        {!allValid && (
          <div className="detail__error" style={{ margin: '0.6rem 1.65rem 0 1.485rem' }}>
            A ring crosses itself (shown red). Fix the shape or cancel — an
            invalid polygon cannot be saved.
          </div>
        )}
      </div>
      <footer className="detail__footer">
        {error && <div className="detail__error">{error}</div>}
        <div className="detail__actions">
          <button className="btn btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn--primary" onClick={onSave} disabled={!allValid || saving}>
            {saving ? 'Saving…' : isOsm ? 'Save as custom copy' : 'Save shape'}
          </button>
        </div>
      </footer>
    </aside>
  );
}

// Sidebar panel for the vineyard split tool: cut lines are drawn on the
// map (SplitLayer); this holds instructions, the parcel preview summary and
// the confirm / cancel / undo actions. Nothing touches the database until
// "Create parcels"; after that, "Undo split" rolls the whole batch back.
function SplitPanel({ session, onCancel, onFinishLine, onPreview, onBack, onConfirm, onRollback, onDone }) {
  const { stage, lines, open, parts, busy, error, name, osmRef } = session;
  const partsCount = parts?.features?.length ?? 0;
  const totalHa = parts ? parts.features.reduce((a, f) => a + (f.properties?.area_ha ?? 0), 0) : 0;
  return (
    <aside className="detail">
      <header className="detail__header">
        <button className="detail__back" onClick={onCancel}>×</button>
        <div className="detail__title-wrap">
          <h2 className="detail__title">Split Vineyard</h2>
          <p className="detail__sub">{name}</p>
        </div>
      </header>
      <div className="detail__body">
        {stage === 'cut' && (
          <>
            <div className="detail__notice">
              Draw cut lines over the satellite view: click to place points,
              double-click (or the button below) to finish a line, right-click
              or Esc to undo. Every cut must cross the polygon completely —
              start and end outside the dashed outline.
            </div>
            <div className="detail__notice" style={{ margin: '0.6rem 1.65rem 0 1.485rem' }}>
              {lines.length} finished cut line{lines.length === 1 ? '' : 's'}
              {open.length > 0 ? ` · drawing (${open.length} point${open.length === 1 ? '' : 's'})` : ''}
            </div>
            <button className="btn btn--ghost geom-edit__undo" onClick={onFinishLine} disabled={open.length < 2 || busy}>
              Finish cut line
            </button>
          </>
        )}
        {stage === 'preview' && (
          <div className="detail__notice">
            {partsCount} parcels · {totalHa.toFixed(2)} ha total. Each parcel
            becomes its own custom vineyard inheriting the parent's
            Anbaugebiet, Bereich, Einzellage, Großlage and Ort; the parent
            ({osmRef}) is marked out of scope. Nothing is saved until you
            confirm.
          </div>
        )}
        {stage === 'done' && (
          <div className="detail__notice">
            Split complete: {session.createdCount} parcels created and the
            parent ({osmRef}) marked out of scope. Undo reverses the whole
            batch — parcels deleted, parent restored.
          </div>
        )}
      </div>
      <footer className="detail__footer">
        {error && <div className="detail__error">{error}</div>}
        <div className="detail__actions">
          {stage === 'cut' && (
            <>
              <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
              <button className="btn btn--primary" onClick={onPreview} disabled={busy || (lines.length === 0 && open.length < 2)}>
                {busy ? 'Splitting…' : 'Preview split'}
              </button>
            </>
          )}
          {stage === 'preview' && (
            <>
              <button className="btn btn--ghost" onClick={onBack} disabled={busy}>Back</button>
              <button className="btn btn--primary" onClick={onConfirm} disabled={busy}>
                {busy ? 'Creating…' : `Create ${partsCount} parcels`}
              </button>
            </>
          )}
          {stage === 'done' && (
            <>
              <button className="btn btn--ghost" onClick={onRollback} disabled={busy}>
                {busy ? 'Undoing…' : 'Undo split'}
              </button>
              <button className="btn btn--primary" onClick={onDone} disabled={busy}>Done</button>
            </>
          )}
        </div>
      </footer>
    </aside>
  );
}

export default function App() {
  const [mode, setMode] = useState({ kind: 'regions' });
  const [selection, setSelection] = useState({ type: 'none' });
  const [lassoArmed, setLassoArmed] = useState(false);
  const [focusBereich, setFocusBereich] = useState(null);
  const [villageEditMode, setVillageEditMode] = useState(false);
  const [labelEditMode, setLabelEditMode] = useState(false);
  // Region-name labels on the overview: drag-to-reposition (signed in)
  const [regionLabelEditMode, setRegionLabelEditMode] = useState(false);
  const [geoLabelEditMode, setGeoLabelEditMode] = useState(false);
  const [riverLabelEditMode, setRiverLabelEditMode] = useState(false);
  const [mtnIconEditMode, setMtnIconEditMode] = useState(false);
  const [zoomToSelection, setZoomToSelection] = useState(false);
  // Regions overview: sidebar<->map hover sync (map greys non-hovered
  // regions; sidebar highlights the row hovered on the map)
  const [hoveredRegion, setHoveredRegion] = useState(null);
  useEffect(() => { setHoveredRegion(null); }, [mode]);
  // Region view: sidebar-list hover -> temporary map highlight (one-way).
  // Village hover shows its dot regardless of the Villages toggle; vineyard
  // hover renders that polygon in the selected format.
  const [hoveredVillage, setHoveredVillage] = useState(null);
  const [hoveredVineyardId, setHoveredVineyardId] = useState(null);
  useEffect(() => { setHoveredVillage(null); setHoveredVineyardId(null); }, [mode]);
  // Einzellage-match highlight (Geography toggle): the four fields that
  // must match exactly, or null when off. Matching ids are derived
  // locally from the loaded FC — the map features carry all four fields.
  const [matchHighlight, setMatchHighlight] = useState(null);
  useEffect(() => { setMatchHighlight(null); }, [mode, focusBereich]);
  // Toggle reason: 'off' | 'pinned' | 'transient'
  const [villageToggle, setVillageToggle] = useState('off');
  // atlas default: michigan shows winery markers whenever a region is
  // entered; germany starts hidden (eye toggle unchanged in both)
  const PRODUCER_TOGGLE_DEFAULT = ATLAS.features.producersOnByDefault ? 'pinned' : 'off';
  const [producerToggle, setProducerToggle] = useState(PRODUCER_TOGGLE_DEFAULT);
  const showVillages = villageToggle !== 'off';
  const showProducers = producerToggle !== 'off';
  const [showLabels, setShowLabels] = useState(true);
  const [showAllVineyards, setShowAllVineyards] = useState(true);
  // The Key Villages eye, Notable-labels eye and GWS & VDP filter reset to
  // their defaults on region/bereich navigation — except toggles set from
  // the FULL region view, which survive bereich moves within that region
  // (keepsToggle). Each user flip records where it happened; the producers
  // eye deliberately stays persistent.
  const toggleOriginsRef = useRef({ villages: null, labels: null, filter: null });
  const markToggleOrigin = (key) => {
    toggleOriginsRef.current[key] = mode.kind === 'region'
      ? { region: mode.name, full: !focusBereich }
      : null;
  };
  const prevNavRef = useRef({ region: null, bereich: null });
  useEffect(() => {
    const next = { region: mode.kind === 'region' ? mode.name : null, bereich: focusBereich };
    const prev = prevNavRef.current;
    prevNavRef.current = next;
    if (next.region === prev.region && next.bereich === prev.bereich) return;
    const o = toggleOriginsRef.current;
    if (!keepsToggle(o.villages, prev, next)) setVillageToggle('off');
    if (!keepsToggle(o.labels, prev, next)) setShowLabels(true);
    if (!keepsToggle(o.filter, prev, next)) setShowAllVineyards(true);
    if (next.region !== prev.region) toggleOriginsRef.current = { villages: null, labels: null, filter: null };
  }, [mode.kind, mode.name, focusBereich]);
  const [showOutOfScope, setShowOutOfScope] = useState(false);
  // Satellite base layer; defaults to map on load, then holds the user's
  // choice across mode/region changes until toggled again.
  const [satellite, setSatellite] = useState(false);
  // Climate overlay: null | 'temp' | 'sun'. Persists like the satellite toggle.
  const [climateLayer, setClimateLayer] = useState(null);
  const [flurbLayer, setFlurbLayer] = useState(false);
  // RLP Weinbergsrolle layer: local-only (data served by the 127.0.0.1:8787
  // bridge from reference/rlp_einzellagen.geojson), signed-in users only.
  const [rlpLagenLayer, setRlpLagenLayer] = useState(false);
  // Key Producers layer (overview only): every producer nationwide on the
  // map, region outlines/labels dimmed to a backdrop and inert.
  // producerChain marks a region entered by clicking one of them — the
  // map-bar back then returns to this overview instead of plain regions.
  // Deep link: landing on /key-producers opens the view directly.
  const [producersLayer, setProducersLayer] = useState(() => window.location.pathname.startsWith('/key-producers'));
  // Key Rivers & Mountains: overview-only overlay on the plain Map basemap.
  // Deep link: landing on /key-rivers-mountains opens the view directly.
  const [geoLayer, setGeoLayer] = useState(() => ATLAS.features.geo && window.location.pathname.startsWith('/key-rivers-mountains'));
  // bumped to snap the map back to the default overview frame (geo search)
  const [overviewFitSignal, setOverviewFitSignal] = useState(0);
  const [producerChain, setProducerChain] = useState(false);
  // Mirror the view in the URL (impressum convention) so the link stays
  // shareable while the layer is active on the overview.
  useEffect(() => {
    const onIt = window.location.pathname.startsWith('/key-producers');
    const active = producersLayer && mode.kind === 'regions';
    if (active && !onIt) window.history.replaceState(null, '', '/key-producers');
    else if (!active && onIt) window.history.replaceState(null, '', '/');
  }, [producersLayer, mode.kind]);
  useEffect(() => {
    const onIt = window.location.pathname.startsWith('/key-rivers-mountains');
    const active = geoLayer && mode.kind === 'regions';
    if (active && !onIt) window.history.replaceState(null, '', '/key-rivers-mountains');
    else if (!active && onIt) window.history.replaceState(null, '', '/');
  }, [geoLayer, mode.kind]);
  // Study Mode (flash cards, signed-in only): full-interface overlay on
  // the centered overview map. Deep link: /study (+ /study/<region>).
  const [studyMode, setStudyMode] = useState(() => ATLAS.features.study && window.location.pathname.startsWith('/study'));
  useEffect(() => {
    const onIt = window.location.pathname.startsWith('/study');
    if (studyMode && !onIt) window.history.replaceState(null, '', '/study');
    else if (!studyMode && onIt) window.history.replaceState(null, '', '/');
  }, [studyMode]);

  // Winery Trip Planner (atlas-gated; michigan): dedicated
  // /winery-trip-planner deep link opens the planner over its region
  // with vineyards hidden.
  const [tripMode, setTripMode] = useState(() => !!ATLAS.tripPlanner && window.location.pathname.startsWith('/winery-trip'));
  const [tripPlan, setTripPlan] = useState(null); // {start, route} from TripPlanner
  useEffect(() => {
    const onIt = window.location.pathname.startsWith('/winery-trip');
    if (tripMode && !onIt) window.history.replaceState(null, '', '/winery-trip-planner');
    else if (!tripMode && onIt) window.history.replaceState(null, '', '/');
  }, [tripMode]);
  // Sidebar-row hover -> that producer's map marker amplifies
  const [hoveredKeyProducer, setHoveredKeyProducer] = useState(null);
  useEffect(() => { setHoveredKeyProducer(null); }, [producersLayer, mode]);
  // Sidebar filter -> matched producer ids (null = no filter): matches
  // grow on the map, the rest mute
  const [producerFilterIds, setProducerFilterIds] = useState(null);
  useEffect(() => { if (!producersLayer) setProducerFilterIds(null); }, [producersLayer]);
  const [rlpLagenFC, setRlpLagenFC] = useState(null);
  const [rlpBridgeOk, setRlpBridgeOk] = useState(false);
  // rlp lage ids already imported as custom vineyards (tags.rlp_lage_id) —
  // those render green (normal app vineyards), not blue.
  const [addedLageIds, setAddedLageIds] = useState(new Set());
  // High-res per-region temperature (25 m). When viewing a region that has one,
  // the temp layer swaps from the Germany-wide 1 km image to this. Overview
  // keeps the 1 km version.
  const [tempRegions, setTempRegions] = useState(null);
  useEffect(() => {
    fetch('/climate/temp-regions.json').then(r => r.json()).then(setTempRegions).catch(() => {});
  }, []);
  const tempRegion = mode.kind === 'region' && tempRegions ? tempRegions.regions[mode.name] : null;
  // Monthly precipitation: per-region overlays, offered only where generated.
  const [precipRegions, setPrecipRegions] = useState(null);
  useEffect(() => {
    fetch('/climate/precip/precip-regions.json').then(r => r.json()).then(setPrecipRegions).catch(() => {});
  }, []);
  const precipRegion = mode.kind === 'region' && precipRegions ? precipRegions.regions[mode.name] : null;
  // Terrain = zoomable Esri hillshade basemap; peer of satellite, available
  // everywhere. Default view on load is the plain Map. Basemap
  // (map/satellite/terrain) and climate overlay are one exclusive picker
  // selection.
  const [terrain, setTerrain] = useState(false);
  // Official Franken Lagen map (Regierung von Unterfranken 1:175k) as a true
  // base layer — logged-in users, Franken region only.
  const [frankenMap, setFrankenMap] = useState(false);
  // Fly-through tours (desktop only): helicopter-launched valley flights.
  // start: journey origin + first-leg direction along the baked route.
  const FLY_TOURS = {
    Ahr: [{
      key: 'ahr', region: 'Ahr',
      title: 'Ahr Valley: fly the river',
      label: 'Tour the Ahr',
      welcome: "You're about to fly up the Ahr valley from the Rhein to Altenburg and back, past its key vineyards.",
      heliport: [50.5575192190523, 7.135880504250146],
      start: { at: 1, dir: -1, welcome: true },
      flightModel: 'helicopter',
      cameraMode: 'pilot',
      // pilot feel like the Mosel tours: zoom 15 (low over the narrow
      // valley, as on the Calmont), pitch 74, and bow reined in from 600
      // to 120 — the old wide bow moved the EYE into the valley walls
      tuning: { bow: 120, slowFloor: 1.0, zoom: 15, pitch: 74, speed: 0.012, yawRate: 15, rollMax: 7, fog: 1.0 },
      outSet: new Set(['Heimersheim', 'Ahrweiler', 'Marienthal', 'Rech']),
    }],
    Mosel: [{
      key: 'mosel', region: 'Mosel',
      title: 'Mittelmosel: fly the river',
      label: 'Tour the Mittelmosel',
      welcome: "You're about to fly the Mittelmosel from Kröv to Klüsserath and back, past the great walls of Ürzig, Bernkastel, Brauneberg and Piesport.",
      heliport: [49.971594824067736, 7.111343151871788],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      cameraMode: 'pilot',   // eye rides the river line (experimental here first)
      // Smooth path only: no POI nose-swings, no bend zoom, and a gentle
      // slowdown floor — the route now follows the true river line with
      // outer-bank vias at the six big bends, so deep speed bleeds are
      // unnecessary and read as pumping. Speed compensates the truer,
      // 20% longer line (42.6 km vs the old corner-cut 35.6).
      poiFacing: false,
      tuning: { slowFloor: 1.0, zoom: 13.8, pitch: 74, yawRate: 15, rollMax: 25, fog: 1.0, lookahead: 350, speed: 0.01 },
    }, {
      key: 'calmont', region: 'Mosel',
      title: 'Calmont: fly the river',
      label: 'Tour the Calmont',
      welcome: "You're about to fly the Terrassenmosel from Senheim past the Calmont, Europe's steepest vineyard, around the Zell hairpin to Reil, and back.",
      heliport: [50.14830203637785, 7.154828631417847],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      cameraMode: 'pilot',
      // smoothed like the Mittelmosel: gentle bleed (0.25 lingered at the
      // loop), nose holds the river line, no POI swings
      poiFacing: false,
      // lookahead 700: under the pilot camera the perceived turn is set by
      // nose lag — the longer lookahead starts the Calmont swing ~350 m
      // earlier, before the wall instead of past it
      tuning: { slowFloor: 1.0, zoom: 15, pitch: 74, yawRate: 15, rollMax: 25, fog: 1.0, lookahead: 700, speed: 0.01, bow: 120 },
    }],
    Rheinhessen: [{
      key: 'roterhang', region: 'Rheinhessen',
      title: 'Roter Hang: fly the Rhein slope',
      label: 'Tour the Roter Hang',
      welcome: "You're about to fly from Dexheim over the Orbel to the Rhein, downstream to Nackenheim, then back south along the Roter Hang over Rothenberg, Hipping, Paterberg, Kreuz and the Tafelstein.",
      heliport: [49.84634520464313, 8.378229133344082],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      cameraMode: 'pilot',
      // the return is baked into the route (river out, slope back): one
      // single pass, and calm steady tuning — slowdown off also disables
      // the POI swings and bend zoom that made this route feel jittery
      oneWay: true,
      // the east bank is Hessen: the tour tiles mix RLP and HVBG imagery
      attribution: '©GeoBasis-DE / LVermGeoRP · ©HVBG, dl-de/by-2-0',
      // pilot feel like the Ahr/Mittelhaardt (a gentle one-way slope run):
      // zoom 15 low over the red slope, pitch 74, bow capped 120 so the
      // low-altitude eye stays off the slope, speed eased for the altitude
      tuning: { slowFloor: 1.0, zoom: 15, pitch: 74, speed: 0.012, yawRate: 15, rollMax: 7, fog: 1.0, bow: 120 },
    }],
    Mittelrhein: [{
      key: 'mittelrhein', region: 'Mittelrhein',
      title: 'Mittelrhein: fly the gorge',
      label: 'Tour the Mittelrhein',
      welcome: "You're about to fly the Rhein gorge from Lorch to Koblenz, past Bacharach, Kaub, Oberwesel, the Loreley, St. Goar and the Boppard loop, landing at the Deutsches Eck.",
      heliport: [50.01413664271699, 7.893509556650194],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      oneWay: true,
      // right bank at Lorch/Lorchhausen is Hessen (HVBG ortho + GLO-30 terrain)
      attribution: '©GeoBasis-DE / LVermGeoRP · ©HVBG, dl-de/by-2-0',
      // calm cruise, no POI nose-pulling (slowdown off keeps turniness 0);
      // speed scaled down so the 48.6 km gorge flies at the same visual
      // pace as the shorter tours
      tuning: { slowFloor: 1.0, pitch: 70, yawRate: 15, fog: 1.0, speed: 0.011 },
    }],
    Rheingau: [{
      key: 'rheingau', region: 'Rheingau',
      title: 'Rheingau: fly the right bank',
      label: 'Tour the Rheingau',
      welcome: "You're about to fly the Rheingau from Wiesbaden straight along the right bank past Eltville, Oestrich-Winkel, Geisenheim and Rüdesheim, then round the Binger Loch and up the gorge to Bacharach.",
      heliport: [50.014446585217165, 7.893152877353003],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      oneWay: true,
      // Hessen bank: HVBG ortho, GLO-30 soft terrain; RLP left bank
      attribution: '©HVBG · ©GeoBasis-DE / LVermGeoRP, dl-de/by-2-0',
      tuning: { slowFloor: 1.0, pitch: 70, yawRate: 15, fog: 1.0, speed: 0.014 },
    }],
    Pfalz: [{
      key: 'mittelhaardt', region: 'Pfalz',
      title: 'Pfalz: Fly the Mittelhaardt',
      label: 'Tour the Mittelhaardt',
      welcome: "You're about to fly the Mittelhaardt north from Neustadt an der Weinstraße, hugging the Haardt tree line past Deidesheim, Forst, Wachenheim, Bad Dürkheim and Kallstadt to Weisenheim am Berg.",
      heliport: [49.334442576684, 8.209026296039273],
      start: { at: 0, dir: 1, welcome: true },
      flightModel: 'helicopter',
      cameraMode: 'pilot',
      oneWay: true,
      // pilot feel like the Ahr (the most similar tour — a gentle one-way
      // run along a slope): zoom 15 low over the tree line, pitch 74, bow
      // reined to 120 (the default 380 would swing the EYE into the trees),
      // speed eased for the lower altitude
      tuning: { slowFloor: 1.0, zoom: 15, pitch: 74, speed: 0.012, yawRate: 15, rollMax: 7, fog: 1.0, bow: 120 },
    }],
  };
  const [flythrough, setFlythrough] = useState(null);   // null | tour config
  const flyTours = (mode.kind === 'region' && !isMobileLayout()) ? (FLY_TOURS[mode.name] ?? []) : [];
  useEffect(() => {
    if (flythrough && !flyTours.some(t => t.key === flythrough.key)) setFlythrough(null);
  }, [flythrough, flyTours]);
  const selectView = useCallback((key) => {
    setSatellite(key === 'satellite');
    setTerrain(key === 'terrain');
    setFrankenMap(key === 'lagen');
    setClimateLayer(key === 'temp' || key === 'sun' || key === 'precip' ? key : null);
    setFlurbLayer(key === 'flurb');
    setRlpLagenLayer(key === 'rlplagen');
    setProducersLayer(key === 'producers');
    // Rivers & Mountains rides the plain Map basemap; any other pick drops it.
    setGeoLayer(key === 'geo');
    // Picking any other layer exits the producer back-chain: zoom and
    // region stay put, back buttons revert to the normal hierarchy. The
    // producers eye itself stays on until back-to-regions or manual off.
    if (key !== 'producers') setProducerChain(false);
  }, []);
  // Precipitation only exists per generated region; drop it when leaving one.
  useEffect(() => {
    if (climateLayer === 'precip' && !precipRegion) selectView('map');
  }, [climateLayer, precipRegion, selectView]);
  // Georeferenced against vineyard-cluster control points; logged-in users can
  // drag-adjust, persisted locally.
  const [lagenBounds, setLagenBounds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('franken-lagen-bounds'));
      if (Array.isArray(saved) && saved.length === 2) return saved;
    } catch { /* fall through to default */ }
    return DEFAULT_LAGEN_BOUNDS;
  });
  const onLagenBoundsChange = useCallback((b) => {
    setLagenBounds(b);
    try { localStorage.setItem('franken-lagen-bounds', JSON.stringify(b)); } catch { /* ignore */ }
    console.log('[lagen] adjusted bounds:', JSON.stringify(b));
  }, []);
  const [mapZoom, setMapZoom] = useState(7);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  // ── Mobile bottom sheet: three snap states. Dragged from the handle (mouse)
  // or from anywhere in the sheet (touch, when its content isn't scrolling).
  // The drag moves a transform, not the height — compositor-only, no per-frame
  // reflow; panel content re-sizes once on snap.
  const [sheetPos, setSheetPos] = useState('mid'); // 'min' | 'mid' | 'max'
  const sheetRef = useRef(null);
  const sheetDragRef = useRef(null);
  // Snap heights; must mirror the --sheet-*-h CSS vars.
  const sheetHeights = () => {
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const inset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom')) || 0;
    return { min: 88 + inset, mid: Math.round(vh * 0.42), max: Math.round(vh * 0.9) };
  };
  // Visible sheet height = distance from the sheet's (transformed) top edge to
  // the bottom of the *visual* viewport.
  const sheetVisibleHeight = () => {
    const vv = window.visualViewport;
    const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    return bottom - sheetRef.current.getBoundingClientRect().top;
  };
  const beginSheetDrag = useCallback((y) => {
    sheetDragRef.current = {
      startY: y, startH: sheetVisibleHeight(),
      lastY: y, lastT: performance.now(), vel: 0
    };
    sheetRef.current.classList.add('sidebar-wrap--dragging');
    document.body.style.userSelect = 'none'; // pills/labels outside the sheet
    document.body.style.webkitUserSelect = 'none';
  }, []);
  const moveSheetDrag = useCallback((y) => {
    const d = sheetDragRef.current;
    if (!d) return;
    const now = performance.now();
    d.vel = (y - d.lastY) / Math.max(1, now - d.lastT); // px/ms, + = down
    d.lastY = y; d.lastT = now;
    const { min, max } = sheetHeights();
    const h = Math.min(max, Math.max(min, d.startH + (d.startY - y)));
    sheetRef.current.style.transform = `translateY(${max - h}px)`;
  }, []);
  const endSheetDrag = useCallback(() => {
    const d = sheetDragRef.current;
    if (!d) return;
    sheetDragRef.current = null;
    const wrap = sheetRef.current;
    const h = sheetVisibleHeight();
    const s = sheetHeights();
    const order = ['min', 'mid', 'max'];
    // Mid is sticky: minimizing takes a deliberate near-full drag down (the
    // bottom quarter of the min–mid span); above that, mid vs max by nearest.
    let next = h < s.min + 0.25 * (s.mid - s.min) ? 'min'
      : h < (s.mid + s.max) / 2 ? 'mid' : 'max';
    if (Math.abs(d.vel) > 0.4) {
      if (d.vel < 0) {
        // Upward flick: nudge one state up
        next = order[Math.min(2, order.indexOf(next) + 1)];
      } else if (next !== 'min') {
        // Downward flick: nudge down but never into min — reaching the
        // minimized strip always requires the full drag to the bottom.
        next = order[Math.max(1, order.indexOf(next) - 1)];
      }
    }
    setSheetPos(next);
    wrap.classList.remove('sidebar-wrap--dragging');
    wrap.style.transform = ''; // CSS var takes over; the transition animates the snap
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
  }, []);
  // Handle drag for mouse pointers only — touches (including on the handle)
  // go through the wrapper-level touch listeners below, so the two never
  // drive the same gesture.
  const onSheetDown = useCallback((e) => {
    if (e.pointerType !== 'mouse' || sheetDragRef.current || !sheetRef.current) return;
    beginSheetDrag(e.clientY);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [beginSheetDrag]);
  const onSheetMove = useCallback((e) => {
    if (e.pointerType !== 'mouse') return;
    moveSheetDrag(e.clientY);
  }, [moveSheetDrag]);
  const onSheetUp = useCallback((e) => {
    if (e.pointerType !== 'mouse') return;
    endSheetDrag();
  }, [endSheetDrag]);

  // Touch: drag the sheet from anywhere inside it. A downward drag takes over
  // when the touched content isn't mid-scroll (its scroller is at the top);
  // upward drags from non-scrollable areas (handle, headers) also drag.
  // Non-passive touchmove so we can preventDefault once the drag claims the
  // gesture; plain taps and content scrolling pass through untouched.
  useEffect(() => {
    const wrap = sheetRef.current;
    if (!wrap) return;
    let startY = 0, decided = false, scroller = null, fromHandle = false;
    const findScroller = (el) => {
      while (el && el !== wrap) {
        const s = getComputedStyle(el);
        if (/(auto|scroll)/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
        el = el.parentElement;
      }
      return null;
    };
    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      decided = false;
      fromHandle = !!e.target.closest?.('.sheet-handle');
      scroller = fromHandle ? null : findScroller(e.target);
    };
    const onMove = (e) => {
      if (e.touches.length !== 1) return;
      const y = e.touches[0].clientY;
      if (sheetDragRef.current) { e.preventDefault(); moveSheetDrag(y); return; }
      if (decided) return; // gesture already ceded to content scrolling
      const dy = y - startY;
      if (Math.abs(dy) < 6) return;
      decided = true;
      const canDrag = fromHandle
        || (dy > 0 && (!scroller || scroller.scrollTop <= 0)) // down: not mid-scroll
        || (dy < 0 && !scroller);                             // up: non-scrollable area
      if (canDrag) {
        beginSheetDrag(startY);
        e.preventDefault();
        moveSheetDrag(y);
      }
    };
    const onEnd = () => { endSheetDrag(); decided = false; };
    wrap.addEventListener('touchstart', onStart, { passive: true });
    wrap.addEventListener('touchmove', onMove, { passive: false });
    wrap.addEventListener('touchend', onEnd);
    wrap.addEventListener('touchcancel', onEnd);
    return () => {
      wrap.removeEventListener('touchstart', onStart);
      wrap.removeEventListener('touchmove', onMove);
      wrap.removeEventListener('touchend', onEnd);
      wrap.removeEventListener('touchcancel', onEnd);
    };
  }, [beginSheetDrag, moveSheetDrag, endSheetDrag]);

  // Minimized: the strip should always show the top of the panel (title), not
  // wherever the content was scrolled to; gesture scrolling is disabled by
  // CSS in this state.
  useEffect(() => {
    if (sheetPos !== 'min') return;
    for (const el of document.querySelectorAll('.sidebar__body, .detail__body')) el.scrollTop = 0;
  }, [sheetPos]);

  // Chrome (iOS/Android) can overlay its bottom toolbar on the layout
  // viewport, pushing a bottom-anchored sheet under it. Fixed elements anchor
  // to the layout viewport (documentElement.clientHeight — NOT innerHeight,
  // which tracks the visual viewport on iOS and always measures ~0 gap), so
  // lift the sheet by however far that extends past the visible area.
  // Safari: gap ≈ 0. Capped, and skipped while an input is focused, so the
  // on-screen keyboard doesn't count as browser chrome.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      if (document.activeElement && /^(INPUT|TEXTAREA)$/.test(document.activeElement.tagName)) return;
      const layoutBottom = document.documentElement.clientHeight;
      const visibleBottom = vv.offsetTop + vv.height;
      const gap = Math.min(140, Math.max(0, Math.round(layoutBottom - visibleBottom)));
      document.documentElement.style.setProperty('--vv-gap', `${gap}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // The search bar is open by default in every view (the .modes row sits
  // clear of the sidebar), so opening it never touches the sidebar state.
  const openSearch = useCallback(() => { setSearchOpen(true); }, []);
  const closeSearch = useCallback(() => { setSearchOpen(false); }, []);
  const openSidebar = useCallback(() => { setSidebarOpen(true); setSearchOpen(false); }, []);
  // landing on /impressum opens the info popup in its impressum view
  // (the HelpModal reads the path itself)
  const [helpOpen, setHelpOpen] = useState(() => window.location.pathname.startsWith('/impressum'));
  const [flyToPoint, setFlyToPoint] = useState(null);
  const [previewFeature, setPreviewFeature] = useState(null);
  const { user, loading: authLoading, signIn, signOut } = useAuth();
  // Anonymous visitors read the baked CDN data; signed-in stays live
  const keyItems = useKeyItems(!user);

  // Lagen map: logged-in + Franken only; fall back to the default view when
  // leaving the region or signing out.
  const lagenAvailable = !!user && mode.kind === 'region' && mode.name === 'Franken';
  useEffect(() => {
    if (frankenMap && !lagenAvailable) selectView('map');
  }, [frankenMap, lagenAvailable, selectView]);


  const { data, dataVersion, loading, error, refetch, patchFeature } = useMapData(mode, !user);

  // Manual refresh (top-left button): refetches the current view. Region
  // blob shapes are rebuilt separately by the local script
  // scripts/refresh_region_blobs.py (npm run region-blobs) — heavy geometry
  // does not belong behind the ~60 s API gateway.
  const refreshData = useCallback(() => { refetch(); }, [refetch]);

  // After a single-vineyard save: fetch just that vineyard's map feature and
  // patch it into the region FeatureCollection — no whole-region reload. Falls
  // back to the full refetch if the RPC fails, or while the out-of-scope layer
  // is visible (a save can move a vineyard between the two collections).
  const patchVineyardFeature = useCallback(async (id) => {
    if (mode.kind !== 'region') return;
    if (showOutOfScope) { refetch(); return; }
    const { data: feature, error } = await supabase.rpc('vineyard_map_feature', { p_id: id });
    if (error) { refetch(); return; }
    patchFeature(id, feature);
  }, [mode.kind, showOutOfScope, refetch, patchFeature]);

  // ── Custom-vineyard geometry editor ──
  // Session: { id, name, rings: [[ [lat,lng], ... ], ...] } (exterior rings,
  // no closing duplicate). Pure geometry edit: saves via refresh_relation_geom
  // (WKT), touching nothing else on the row.
  const [geomEdit, setGeomEdit] = useState(null);
  const [geomEditSaving, setGeomEditSaving] = useState(false);
  const [geomEditError, setGeomEditError] = useState(null);
  const [detailBump, setDetailBump] = useState(0);

  const startGeomEdit = useCallback((v) => {
    const polys = v.geometry?.type === 'MultiPolygon' ? v.geometry.coordinates
                : v.geometry?.type === 'Polygon' ? [v.geometry.coordinates] : [];
    const rings = polys.map((p) => {
      const r = (p[0] ?? []).slice();
      if (r.length > 1) {
        const [f, l] = [r[0], r[r.length - 1]];
        if (f[0] === l[0] && f[1] === l[1]) r.pop();
      }
      return r.map(([lng, lat]) => [lat, lng]);
    }).filter(r => r.length >= 3);
    if (rings.length === 0) return;
    setGeomEditError(null);
    setGeomEdit({
      id: v.id,
      osmType: v.osm_type,
      osmRef: `${v.osm_type}/${v.osm_id}`,
      name: v.canonical_name_de || v.full_label_name || v.einzellage || 'Unnamed vineyard',
      rings,
    });
  }, []);

  // ── Vineyard split tool ──────────────────────────────────────────────
  // Manual cut lines over satellite imagery chop a big relation/way/custom
  // into standalone parcels (Option 3 of the split design). Server does the
  // geometry (split_vineyard_preview / apply_vineyard_split); children
  // inherit the parent's locational fields and carry tags.split_batch so
  // rollback_vineyard_split can reverse the whole operation.
  const [splitSession, setSplitSession] = useState(null);

  const startSplit = useCallback((v) => {
    if (!v?.geometry) return;
    selectView('satellite');
    setSplitSession({
      id: v.id,
      osmRef: `${v.osm_type}/${v.osm_id}`,
      name: v.canonical_name_de || v.full_label_name || v.einzellage || 'Unnamed vineyard',
      geometry: v.geometry,
      lines: [], open: [],
      stage: 'cut', parts: null, busy: false, error: null,
      batch: null, createdCount: 0,
    });
  }, [selectView]);

  const splitAddPoint = useCallback((pt) => {
    setSplitSession(s => (s && s.stage === 'cut' ? { ...s, ...cutAddPoint(s, pt) } : s));
  }, []);
  const splitUndoPoint = useCallback(() => {
    setSplitSession(s => (s && s.stage === 'cut' ? { ...s, ...cutUndo(s) } : s));
  }, []);
  const splitFinishLine = useCallback(() => {
    setSplitSession(s => (s && s.stage === 'cut' ? { ...s, ...cutFinishLine(s) } : s));
  }, []);

  const splitPreview = useCallback(async () => {
    const s = splitSession;
    if (!s || s.busy) return;
    const lines = cutEffectiveLines(s);
    if (lines.length === 0) return;
    setSplitSession(x => x && { ...x, busy: true, error: null });
    const { data, error } = await supabase.rpc('split_vineyard_preview', {
      p_id: s.id,
      p_lines: cutLinesToGeoJSON(lines),
    });
    setSplitSession(x => {
      if (!x) return x;
      if (error) return { ...x, busy: false, error: error.message };
      if ((data?.features?.length ?? 0) < 2) {
        return { ...x, busy: false, error: 'The cuts did not divide the polygon — every line must cross it completely.' };
      }
      return { ...x, busy: false, stage: 'preview', parts: data, lines, open: [] };
    });
  }, [splitSession]);

  const splitBack = useCallback(() => {
    setSplitSession(s => (s ? { ...s, stage: 'cut', parts: null, error: null } : s));
  }, []);

  const splitConfirm = useCallback(async () => {
    const s = splitSession;
    if (!s?.parts || s.busy) return;
    setSplitSession(x => x && { ...x, busy: true, error: null });
    const { data, error } = await supabase.rpc('apply_vineyard_split', {
      p_parent: s.id,
      p_parts: s.parts.features.map(f => f.geometry),
    });
    if (error) {
      setSplitSession(x => x && { ...x, busy: false, error: error.message });
      return;
    }
    refetch();
    setSplitSession(x => x && { ...x, busy: false, stage: 'done', batch: data?.batch ?? null, createdCount: data?.count ?? 0 });
  }, [splitSession, refetch]);

  const splitRollback = useCallback(async () => {
    const s = splitSession;
    if (!s?.batch || s.busy) return;
    setSplitSession(x => x && { ...x, busy: true, error: null });
    const { error } = await supabase.rpc('rollback_vineyard_split', { p_batch: s.batch });
    if (error) {
      setSplitSession(x => x && { ...x, busy: false, error: error.message });
      return;
    }
    refetch();
    setSplitSession(null);
  }, [splitSession, refetch]);

  const splitDone = useCallback(() => {
    setSplitSession(null);
    setSelection({ type: 'none' });
  }, []);

  const geomEditRingValid = useMemo(
    () => (geomEdit ? geomEdit.rings.map(r => r.length >= 3 && !ringSelfIntersects(r)) : []),
    [geomEdit]
  );
  const geomEditAllValid = geomEditRingValid.every(Boolean);

  // every mutation snapshots the previous rings (capped at 50 undo steps)
  const withHistory = (s, rings) =>
    ({ ...s, rings, history: [...(s.history ?? []).slice(-49), s.rings] });

  const moveGeomVertex = useCallback((pi, vi, latlng) => {
    setGeomEdit(s => {
      if (!s) return s;
      const rings = s.rings.map((r, i) => (i === pi ? r.map((p, j) => (j === vi ? latlng : p)) : r));
      return withHistory(s, rings);
    });
  }, []);

  // Insert a new vertex on the segment AFTER index vi (midpoint handles).
  const insertGeomVertex = useCallback((pi, vi, latlng) => {
    setGeomEdit(s => {
      if (!s) return s;
      const rings = s.rings.map((r, i) => {
        if (i !== pi) return r;
        const out = r.slice();
        out.splice(vi + 1, 0, latlng);
        return out;
      });
      return withHistory(s, rings);
    });
  }, []);

  // Shift-lasso deletion: drop the selected vertices, reclosing each ring.
  // A ring left with fewer than 3 points is removed entirely; refusing only
  // when nothing at all would remain.
  const deleteGeomVertices = useCallback((keys) => {
    setGeomEdit(s => {
      if (!s || keys.length === 0) return s;
      const kill = new Set(keys.map(([pi, vi]) => `${pi}:${vi}`));
      const rings = s.rings
        .map((r, pi) => r.filter((_, vi) => !kill.has(`${pi}:${vi}`)))
        .filter(r => r.length >= 3);
      if (rings.length === 0) return s;
      return withHistory(s, rings);
    });
  }, []);

  const undoGeomEdit = useCallback(() => {
    setGeomEdit(s => {
      const h = s?.history ?? [];
      if (!s || h.length === 0) return s;
      return { ...s, rings: h[h.length - 1], history: h.slice(0, -1) };
    });
  }, []);

  // Every field update_vineyard whitelists that describes the vineyard
  // itself — copied onto the custom fork of an OSM way/relation.
  const GEOM_FORK_FIELDS = [
    'canonical_name_de', 'full_label_name', 'anbaugebiet', 'bereich',
    'grosslage', 'ort', 'ortsteil', 'einzellage', 'gewann_or_katasterlage',
    'wine_register_id', 'origin_tier_current', 'vdp_classification',
    'vdp_gg_eligible', 'official_erstes_gewaechs_eligible',
    'official_grosses_gewaechs_eligible', 'notable', 'main_grape_varieties',
    'gg_allowed_grapes', 'soil_summary', 'slope_mean_pct', 'aspect',
    'altitude_min_m', 'altitude_max_m', 'known_producers', 'benchmark_wines',
    'exam_relevance_level', 'source_urls', 'confidence_score',
    'last_verified', 'notes',
    'has_old_vines', 'old_vine_date_planted', 'old_vine_parcel_ha',
    'old_vine_parcel_ha_approx', 'old_vine_varieties', 'old_vine_producer',
    'old_vine_elevation_m', 'old_vine_source_name',
    'old_vine_match_confidence', 'old_vine_match_method',
  ];

  const saveGeomEdit = useCallback(async () => {
    if (!geomEdit || !geomEditAllValid) return;
    setGeomEditSaving(true);
    setGeomEditError(null);
    try {
      if (geomEdit.osmType === 'custom') {
        // my own polygon: edit it in place
        const wkt = 'MULTIPOLYGON(' + geomEdit.rings.map((r) => {
          const pts = [...r, r[0]].map(([lat, lng]) => `${lng} ${lat}`).join(',');
          return `((${pts}))`;
        }).join(',') + ')';
        const { error } = await supabase.rpc('refresh_relation_geom', {
          p_id: geomEdit.id, p_wkt: wkt,
        });
        if (error) throw error;
        patchVineyardFeature(geomEdit.id);
        setDetailBump(b => b + 1);   // remounts the open detail panel -> refetch
        setGeomEdit(null);
      } else {
        // OSM way/relation: NEVER overwrite true OSM geometry. Fork the
        // edited shape into a new custom polygon carrying all data fields,
        // and retire the OSM original (out of scope) so they don't overlap.
        const { data: full, error: fullErr } = await supabase.rpc('vineyard_detail', { p_id: geomEdit.id });
        if (fullErr) throw fullErr;
        const patch = {};
        for (const k of GEOM_FORK_FIELDS) {
          if (full?.[k] !== null && full?.[k] !== undefined) patch[k] = full[k];
        }
        patch.tags = { ...(full?.tags ?? {}), derived_from: geomEdit.osmRef };
        const geometry = {
          type: 'MultiPolygon',
          coordinates: geomEdit.rings.map((r) => {
            const ring = [...r, r[0]].map(([lat, lng]) => [lng, lat]);
            return [ring];
          }),
        };
        const { data: newId, error: createErr } = await supabase.rpc('create_custom_vineyard', {
          p_geom: geometry, p_patch: patch,
        });
        if (createErr) throw createErr;
        const { error: oosErr } = await supabase.rpc('update_vineyard', {
          p_id: geomEdit.id, p_patch: { out_of_scope: true },
        });
        if (oosErr) throw oosErr;
        setGeomEdit(null);
        refetch();
        setZoomToSelection(false);
        setSelection({ type: 'single', id: newId });
      }
    } catch (e) {
      setGeomEditError(e.message);
    } finally {
      setGeomEditSaving(false);
    }
  }, [geomEdit, geomEditAllValid, patchVineyardFeature, refetch]);

  // The session belongs to the open panel: leaving the vineyard, the region,
  // or the account discards it silently (nothing was written).
  useEffect(() => {
    if (geomEdit && (selection.type !== 'single' || selection.id !== geomEdit.id || !user)) {
      setGeomEdit(null);
    }
  }, [geomEdit, selection, user]);


  // Out-of-scope layer: logged-in only, lazily loaded per region when toggled on.
  const outOfScopeData = useOutOfScopeVineyards(mode, showOutOfScope && !!user);
  useEffect(() => {
    if (!user && showOutOfScope) setShowOutOfScope(false);
  }, [user, showOutOfScope]);

  // ── Pending search result (for cross-region navigation) ──
  const pendingSearchRef = useRef(null);
  const [hasPendingSearch, setHasPendingSearch] = useState(false);
  // Name from the search result, shown instantly in the detail skeleton while
  // the vineyard's data loads (keyed by id so it never labels another panel).
  const [searchHint, setSearchHint] = useState(null);

  // ── Mode transitions clear selection ──
  const switchMode = useCallback((next) => {
    // Cancel any pending cross-region search; a search-initiated switch
    // re-arms it right after calling switchMode. Otherwise a stale pending
    // result could fire on a later region visit and skipFitToData stays stuck.
    pendingSearchRef.current = null;
    setHasPendingSearch(false);
    setMode(next);
    setSelection({ type: 'none' });
    setLassoArmed(false);
    setFocusBereich(null);
    setVillageEditMode(false);
    setLabelEditMode(false);
    setVillageToggle('off');
    setProducerToggle(PRODUCER_TOGGLE_DEFAULT);
    setShowLabels(true);
    setMapZoom(7);
    if (next.kind === 'regions') { setSidebarOpen(true); setShowAllVineyards(true); }
  }, []);

  const selectRegion = useCallback((name) => {
    switchMode({ kind: 'region', name });
  }, [switchMode]);

  // ── Map → selection ──
  const onSelectVineyard = useCallback((id) => {
    setSelection({ type: 'single', id });
  }, []);

  const onLassoFinished = useCallback((ids) => {
    setLassoArmed(false);
    if (ids.length === 0) {
      setSelection({ type: 'none' });
    } else if (ids.length === 1) {
      setSelection({ type: 'single', id: ids[0] });
    } else {
      setSelection({ type: 'multi', ids });
    }
  }, []);

  const onSelectVillage = useCallback((village) => {
    setSelection({ type: 'village', village });
  }, []);

  const onSelectFlurb = useCallback((oid) => {
    setZoomToSelection(false);
    setSelection({ type: 'flurb', oid });
  }, []);

  const onSelectVillageFromMap = useCallback((village) => {
    setZoomToSelection(false);
    setSelection({ type: 'village', village });
  }, []);

  const onSelectProducerFromMap = useCallback((producer) => {
    setZoomToSelection(false);
    setSelection({ type: 'producer', producer });
  }, []);

  // ── Flurbereinigung layer data (scoped to region/bereich when in one) ──
  // The dataset covers Rheinland-Pfalz only: the layer exists solely inside
  // its six regions and is hidden everywhere else.
  const FLURB_REGIONS = ['Ahr', 'Mittelrhein', 'Mosel', 'Nahe', 'Rheinhessen', 'Pfalz'];
  const flurbAvailable = mode.kind === 'region' && FLURB_REGIONS.includes(mode.name);
  // RLP Lagen layer: needs a signed-in user, the local bridge with the
  // register file on disk, and an RLP region view.
  useEffect(() => {
    // Probe the bridge ONLY from a local frontend — from the deployed site
    // a fetch to 127.0.0.1 makes the browser demand local-network access
    // (same gate as the Compute Terrain button).
    if (!user || !['localhost', '127.0.0.1'].includes(window.location.hostname)) { setRlpBridgeOk(false); return; }
    const ctrl = new AbortController();
    fetch('http://127.0.0.1:8787/health', { signal: ctrl.signal })
      .then(r => r.json())
      .then(j => setRlpBridgeOk(!!j.rlp_lagen))
      .catch(() => setRlpBridgeOk(false));
    return () => ctrl.abort();
  }, [user]);
  const rlpLagenAvailable = !!user && rlpBridgeOk && mode.kind === 'region' && FLURB_REGIONS.includes(mode.name);
  useEffect(() => {
    if (rlpLagenLayer && !rlpLagenAvailable) setRlpLagenLayer(false);
  }, [rlpLagenLayer, rlpLagenAvailable]);
  useEffect(() => {
    if (!rlpLagenLayer) return;
    if (!rlpLagenFC) {
      fetch('http://127.0.0.1:8787/rlp_lagen')
        .then(r => r.json())
        .then(setRlpLagenFC)
        .catch(() => setRlpLagenFC(null));
    }
    // which register polygons are already in the app (tags.rlp_lage_id)
    supabase.from('vineyards')
      .select('rlp:tags->>rlp_lage_id')
      .not('tags->>rlp_lage_id', 'is', null)
      .then(({ data, error }) => {
        if (!error && data) setAddedLageIds(new Set(data.map(r => String(r.rlp))));
      });
  }, [rlpLagenLayer]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rlpLagenLayer) {
      setSelection(s => (s.type === 'lage' ? { type: 'none' } : s));
    }
  }, [rlpLagenLayer]);
  const onSelectLage = useCallback((feature) => {
    setZoomToSelection(false);
    setSelection({ type: 'lage', feature });
  }, []);
  useEffect(() => {
    if (flurbLayer && !flurbAvailable) setFlurbLayer(false);
  }, [flurbLayer, flurbAvailable]);
  const flurb = useFlurbereinigung(flurbLayer, mode, focusBereich);
  // Turning the layer off closes any open procedure sidebar.
  useEffect(() => {
    if (!flurbLayer) {
      setSelection(s => (s.type === 'flurb' ? { type: 'none' } : s));
    }
  }, [flurbLayer]);
  // The layer is standalone to the view it was activated in: navigating to
  // the regions overview, into another region (sidebar or search), or out
  // of a region auto-toggles it off, and vineyard colors return to normal.
  // (Picking another basemap/overlay already deactivates it via selectView.)
  useEffect(() => {
    if (regionLabelEditMode && (mode.kind !== 'regions' || !user)) setRegionLabelEditMode(false);
  }, [regionLabelEditMode, mode.kind, user]);
  useEffect(() => {
    if (geoLabelEditMode && (mode.kind !== 'regions' || !geoLayer || !user)) setGeoLabelEditMode(false);
  }, [geoLabelEditMode, mode.kind, geoLayer, user]);
  useEffect(() => {
    if (riverLabelEditMode && (mode.kind !== 'regions' || !geoLayer || !user)) setRiverLabelEditMode(false);
  }, [riverLabelEditMode, mode.kind, geoLayer, user]);
  useEffect(() => {
    if (mtnIconEditMode && (mode.kind !== 'regions' || !geoLayer || !user)) setMtnIconEditMode(false);
  }, [mtnIconEditMode, mode.kind, geoLayer, user]);

  const flurbScopeRef = useRef(null);
  useEffect(() => {
    const scope = `${mode.kind}|${mode.name ?? ''}`;
    if (!flurbLayer) {
      flurbScopeRef.current = null;
    } else if (flurbScopeRef.current == null) {
      flurbScopeRef.current = scope;
    } else if (flurbScopeRef.current !== scope) {
      flurbScopeRef.current = null;
      setFlurbLayer(false);
    }
  }, [flurbLayer, mode]);

  // ── Draw custom vineyards ──
  // Sessions never touch the DB until save; on success the new vineyard is
  // selected in the normal experience with fresh region data behind it.
  const drawApi = useDrawVineyard({
    onSaved: (id) => {
      refetch();
      setZoomToSelection(false);
      setSelection({ type: 'single', id });
    },
  });
  const drawActive = !!drawApi.session;
  const drawRegionRef = useRef(null);
  const startDraw = useCallback(() => {
    setLassoArmed(false);
    setSelection({ type: 'none' });
    drawRegionRef.current = mode.kind === 'region' ? mode.name : null;
    drawApi.start();
  }, [drawApi, mode]);
  // Lasso multi-selection -> one new multipolygon vineyard: fetch the full
  // geometries, open a draw session on the details stage (same flow as a
  // hand-drawn shape). Errors throw back to the panel's button.
  const makeMultipolygon = useCallback(async (ids) => {
    // the lasso can catch visible out-of-scope polygons (white layer on):
    // they must never join a multipolygon
    const oos = new Set((outOfScopeData?.features ?? []).map(f => f.properties?.id).filter(Boolean));
    const inScope = ids.filter(id => !oos.has(id));
    if (inScope.length === 0) throw new Error('Only out-of-scope vineyards selected');
    const { data, error } = await supabase.rpc('collect_vineyard_geometries', { p_ids: inScope });
    if (error) throw error;
    if (!drawApi.startFromGeometry(data)) throw new Error('No usable geometry in the selection');
    drawRegionRef.current = mode.kind === 'region' ? mode.name : null;
    setLassoArmed(false);
    setSelection({ type: 'none' });
  }, [drawApi, mode, outOfScopeData]);
  // Leaving the screen saves nothing: discard the session silently when the
  // view changes away from the region being drawn in, or on logout.
  useEffect(() => {
    if (!drawActive) return;
    if (mode.kind !== 'region' || mode.name !== drawRegionRef.current || !user) {
      drawApi.discard();
    }
  }, [drawActive, mode, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectVineyardFromMap = useCallback((id) => {
    if (drawApi.session) return; // sidebar is the draw tool space
    setZoomToSelection(false);
    setSelection({ type: 'single', id });
  }, [drawApi.session]);

  const closeSelection = useCallback(() => {
    // A selection opened from another panel (e.g. an affected vineyard from
    // a Flurbereinigung sidebar) carries returnTo: back goes there instead
    // of closing outright.
    setSelection(s => (s.returnTo ? s.returnTo : { type: 'none' }));
    setVillageToggle(t => t === 'transient' ? 'off' : t);
    setProducerToggle(t => t === 'transient' ? PRODUCER_TOGGLE_DEFAULT : t);
  }, []);

  // The map-bar back steps down the view hierarchy, mirroring what the
  // selection panels' own back buttons did: an open panel closes first
  // (honoring its returnTo chain), then a bereich focus lifts, then the
  // region exits — to the Key Producers overview when the region was
  // entered through it (producerChain), else to the plain overview, where
  // one more back dismisses the producers layer itself. Inert during
  // draw/split/geometry sessions — those own their cancel flows.
  const backsSelection = selection.type !== 'none';
  const backsBereich = mode.kind === 'region' && focusBereich && bereicheFor(mode.name).length > 1;
  const goBack = () => {
    if (drawActive || splitSession || geomEdit) return;
    if (backsSelection) {
      // a producer opened FROM the Key Producers view skips the region on
      // the way back: panel closes and the producers overview returns in
      // one step (producersLayer is still on, so landing re-shows it)
      if (selection.type === 'producer' && producerChain && mode.kind === 'region') {
        closeSelection();
        setProducerChain(false);
        switchMode({ kind: 'regions' });
        return;
      }
      closeSelection();
      return;
    }
    if (mode.kind === 'regions') { setProducersLayer(false); setGeoLayer(false); return; }
    if (backsBereich) { setFocusBereich(null); return; }
    if (mode.kind === 'region' && producerChain) {
      // producersLayer is still on: landing on the overview re-shows it
      setProducerChain(false);
      switchMode({ kind: 'regions' });
      return;
    }
    setProducersLayer(false);
    switchMode({ kind: 'regions' });
  };
  const backLabel = backsSelection
    ? (selection.type === 'producer' && producerChain && mode.kind === 'region' ? 'Back to producers' : 'Back')
    : mode.kind === 'regions' ? 'Back to regions'
    : backsBereich ? `Back to ${mode.name}`
    : mode.kind === 'region' && producerChain ? 'Back to producers'
    : 'Back to regions';

  // GWS & VDP Only auto-release: navigating to a filtered-out vineyard
  // (search, twins, drill-down…) would select something the map hides —
  // the filter turns itself off instead.
  useEffect(() => {
    if (showAllVineyards || selection.type !== 'single' || !data?.features) return;
    const f = data.features.find(ft => ft.properties?.id === selection.id);
    if (f && !isKeyVineyard(f.properties)) setShowAllVineyards(true);
  }, [selection, data, showAllVineyards]);

  // ── Execute pending search selection once region data loads ──
  useEffect(() => {
    if (!pendingSearchRef.current || loading) return;
    const p = pendingSearchRef.current;
    // Verify we're in the correct region
    if (mode.kind !== 'region' || mode.name !== p.anbaugebiet) return;
    // Wait for the correct region's data to actually arrive
    if (!data || data.region !== p.anbaugebiet) return;
    pendingSearchRef.current = null;
    setHasPendingSearch(false);
    setPreviewFeature(null);
    if (!isMobile()) setSidebarOpen(true);
    if (p.kind === 'vineyard') {
      setZoomToSelection(!p.preZoomed);
      onSelectVineyard(p.id);
    } else if (p.kind === 'village' && p.latitude && p.longitude) {
      setVillageToggle(t => t === 'off' ? 'transient' : t);
      setZoomToSelection(!p.preZoomed);
      setSelection({ type: 'village', village: {
        id: p.id,
        name: p.label,
        latitude: Number(p.latitude),
        longitude: Number(p.longitude),
        description: p.description,
        anbaugebiet: p.anbaugebiet,
        bereich: p.bereich
      } });
    } else if (p.kind === 'producer' && p.latitude && p.longitude) {
      setProducerToggle(t => t === 'off' ? 'transient' : t);
      setZoomToSelection(!p.preZoomed);
      setSelection({ type: 'producer', producer: { id: p.id, name: p.label, latitude: Number(p.latitude), longitude: Number(p.longitude) } });
    }
  }, [data, loading, mode]);

  const handleSearchResult = useCallback((result) => {
    setSearchOpen(false);
    if (!isMobile()) setSidebarOpen(true);
    // Rivers & mountain ranges live on the overview geo layer: zoom out
    // to the default main view, enter the layer, select the feature. Back
    // then walks panel -> full geo layer -> plain overview.
    if (result.kind === 'river' || result.kind === 'mountain') {
      selectView('geo');
      switchMode({ kind: 'regions' });
      setOverviewFitSignal(s => s + 1);
      setSelection({ type: 'geo', kind: result.kind, name: result.label });
      return;
    }
    // Pre-zoom to coordinates immediately if available (before region data loads)
    const preZoomCoord = (() => {
      // Try search result's own coordinates first
      if (result.latitude && result.longitude) return [Number(result.latitude), Number(result.longitude)];
      // Fall back to preloaded key items
      if (keyItems) {
        const item = keyItems.get(`${result.kind}-${result.id}`);
        if (item?.latitude && item?.longitude) return [Number(item.latitude), Number(item.longitude)];
      }
      return null;
    })();
    const preZoomed = !!(preZoomCoord && !isNaN(preZoomCoord[0]) && !isNaN(preZoomCoord[1]));
    if (preZoomed) {
      setFlyToPoint(preZoomCoord);
    }

    if (result.kind === 'vineyard') setSearchHint({ id: result.id, name: result.label });

    const inCurrentRegion = mode.kind === 'region' && mode.name === result.anbaugebiet;
    if (!inCurrentRegion) {
      // Fetch single vineyard geometry for preview while region loads
      if (result.kind === 'vineyard') {
        supabase.rpc('vineyard_detail', { p_id: result.id }).then(({ data: v }) => {
          if (v?.geometry) {
            setPreviewFeature({
              type: 'Feature',
              geometry: v.geometry,
              properties: { id: v.id, anbaugebiet: v.anbaugebiet, bereich: v.bereich, notable: v.notable }
            });
          }
        });
      }
      // Switch region first (switchMode cancels pending), then arm the
      // pending selection for when the region's data arrives.
      selectRegion(result.anbaugebiet);
      // preZoomed: the map is already framed on the destination — when the
      // region data lands, keep this exact view and only swap the content.
      pendingSearchRef.current = { ...result, preZoomed };
      setHasPendingSearch(true);
      return;
    }
    // Already in the right region — select immediately. Clear any bereich
    // focus (as the cross-region path does via switchMode): a stale focus
    // hides every polygon at a destination in another bereich.
    setFocusBereich(null);
    if (result.kind === 'vineyard') {
      setZoomToSelection(true);
      onSelectVineyard(result.id);
    } else if (result.kind === 'village' && result.latitude && result.longitude) {
      setVillageToggle(t => t === 'off' ? 'transient' : t);
      setZoomToSelection(true);
      setSelection({ type: 'village', village: {
        id: result.id,
        name: result.label,
        latitude: Number(result.latitude),
        longitude: Number(result.longitude),
        description: result.description,
        anbaugebiet: result.anbaugebiet,
        bereich: result.bereich
      } });
    } else if (result.kind === 'producer' && result.latitude && result.longitude) {
      setProducerToggle(t => t === 'off' ? 'transient' : t);
      setZoomToSelection(true);
      setSelection({ type: 'producer', producer: { id: result.id, name: result.label, latitude: Number(result.latitude), longitude: Number(result.longitude) } });
    }
  }, [mode, selectRegion, onSelectVineyard, keyItems, selectView, switchMode]);

  // /?vineyard=<id>: deep link used by Study Mode's "Study X" card links
  // (new tab zoomed to the vineyard). Resolves via key items once loaded,
  // then routes through the normal search-result flow and cleans the URL.
  const vineyardParamRef = useRef(new URLSearchParams(window.location.search).get('vineyard'));
  useEffect(() => {
    const id = vineyardParamRef.current;
    if (!id || !keyItems) return;
    const item = keyItems.get(`vineyard-${id}`);
    if (!item) return;
    vineyardParamRef.current = null;
    window.history.replaceState(null, '', '/');
    handleSearchResult({ kind: 'vineyard', id: item.id, label: item.name, anbaugebiet: item.anbaugebiet, latitude: item.latitude, longitude: item.longitude });
  }, [keyItems, handleSearchResult]);

  // Key Producers layer: all producers nationwide (from the preloaded key
  // items), grouped for the overview sidebar and the map markers.
  const keyProducers = useMemo(() => {
    if (!keyItems) return [];
    return [...keyItems.values()]
      .filter(i => i.kind === 'producer' && i.latitude && i.longitude)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [keyItems]);

  // A Key Producers click (map marker or sidebar row) rides the search
  // flow into the region. Pin the producers eye AFTER handleSearchResult:
  // its switchMode resets the toggle in the same batch, and pinned must
  // win so the eye stays on until back-to-regions or a manual off.
  const selectKeyProducer = useCallback((p) => {
    setProducerChain(true);
    handleSearchResult({
      kind: 'producer', id: p.id, label: p.name, anbaugebiet: p.anbaugebiet,
      latitude: p.latitude, longitude: p.longitude,
    });
    setProducerToggle('pinned');
  }, [handleSearchResult]);

  // Key Rivers & Mountains: a clicked river line/label or mountain opens
  // its description panel over the sidebar.
  const onSelectGeoFeature = useCallback(({ kind, name }) => {
    if (!isMobile()) setSidebarOpen(true);
    setSelection({ type: 'geo', kind, name });
  }, []);

  // ── Selection panel (overlay on top of sidebar) ──
  const selectionPanel = (() => {
    // A searched vineyard/village/producer shows its skeleton immediately —
    // never the region sidebar flashing in while the region data loads.
    if (hasPendingSearch && pendingSearchRef.current) {
      return <DetailSkeleton name={pendingSearchRef.current.label} />;
    }
    if (selection.type === 'producer') {
      return (
        <ProducerPanel
          producer={selection.producer}
          onClose={closeSelection}
          user={user}
        />
      );
    }
    if (selection.type === 'lage') {
      return (
        <RlpLagenPanel
          key={selection.feature.properties.id}
          feature={selection.feature}
          onClose={closeSelection}
          onSaved={(id, lageId) => {
            setAddedLageIds(s => new Set([...s, lageId]));
            refetch();
            setZoomToSelection(false);
            setSelection({ type: 'single', id });
          }}
        />
      );
    }
    if (selection.type === 'flurb') {
      return (
        <FlurbPanel
          key={selection.oid}
          oid={selection.oid}
          user={user}
          proceduresFC={flurb.proceduresFC}
          overlaps={flurb.overlaps}
          onSelectVineyard={(id) => {
            setZoomToSelection(true);
            setSelection({ type: 'single', id, returnTo: { type: 'flurb', oid: selection.oid } });
          }}
          onClose={closeSelection}
        />
      );
    }
    if (splitSession) {
      return (
        <SplitPanel
          session={splitSession}
          onCancel={() => setSplitSession(null)}
          onFinishLine={splitFinishLine}
          onPreview={splitPreview}
          onBack={splitBack}
          onConfirm={splitConfirm}
          onRollback={splitRollback}
          onDone={splitDone}
        />
      );
    }
    if (geomEdit) {
      return (
        <GeometryEditPanel
          session={geomEdit}
          allValid={geomEditAllValid}
          saving={geomEditSaving}
          error={geomEditError}
          onUndo={undoGeomEdit}
          onCancel={() => setGeomEdit(null)}
          onSave={saveGeomEdit}
        />
      );
    }
    if (selection.type === 'geo') {
      return (
        <GeoFeaturePanel
          key={`${selection.kind}-${selection.name}`}
          kind={selection.kind}
          name={selection.name}
          region={mode.kind === 'region' ? mode.name : null}
          onClose={closeSelection}
        />
      );
    }
    if (selection.type === 'village') {
      return (
        <VillagePanel
          key={selection.village.id}
          village={selection.village}
          onClose={closeSelection}
          user={user}
        />
      );
    }
    if (selection.type === 'single') {
      return (
        <DetailPanel
          key={`${selection.id}:${detailBump}`}
          id={selection.id}
          canEdit={!!user && !isMobile()}
          onEditGeometry={startGeomEdit}
          onSplit={startSplit}
          onClose={closeSelection}
          onRefreshed={refetch}
          onFeatureSaved={patchVineyardFeature}
          user={user}
          placeholderName={searchHint?.id === selection.id ? searchHint.name : null}
          showBack={!!flythrough}
          matchHighlight={matchHighlight}
            onToggleMatchHighlight={setMatchHighlight}
            onNavigateResult={handleSearchResult}
          />
      );
    }
    if (selection.type === 'multi') {
      return (
        <BulkEditPanel
          ids={selection.ids}
          canEdit={!!user && !isMobile()}
          onClose={closeSelection}
          onSaved={() => { refetch(); closeSelection(); }}
          mode={mode}
          onMakeMultipolygon={makeMultipolygon}
        />
      );
    }
    return null;
  })();

  const selectedId  = selection.type === 'single' ? selection.id : null;
  // Memoized: a fresh Set identity every render would re-trigger the vineyard
  // layer's restyle effect (setStyle over all visible polygons) on unrelated
  // state changes.
  const matchHighlightIds = useMemo(() => {
    if (!matchHighlight || !data?.features) return null;
    const ids = new Set();
    for (const f of data.features) {
      if (f.properties?.id && matchesSite(f.properties, matchHighlight)) ids.add(f.properties.id);
    }
    return ids;
  }, [matchHighlight, data]);
  // the toggle survives only while a vineyard of the SAME exact site is
  // selected: any other selection (village, producer, flurb, multi, an
  // outside vineyard, or nothing) ends the highlight
  useEffect(() => {
    if (!matchHighlight) return;
    if (selection.type !== 'single') { setMatchHighlight(null); return; }
    if (matchHighlightIds && !matchHighlightIds.has(selection.id)) setMatchHighlight(null);
  }, [selection, matchHighlight, matchHighlightIds]);

  const selectedIds = useMemo(
    () => selection.type === 'multi' ? new Set(selection.ids) : new Set(),
    [selection]
  );

  // Expose diagnostic state for ErrorBoundary logging
  window.__APP_DEBUG = {
    mode, selection, focusBereich, loading,
    hasData: !!data, dataRegion: data?.region,
    showVillages, showProducers, showLabels, showAllVineyards,
    sidebarOpen, searchOpen, mapZoom, satellite, terrain, climateLayer, sheetPos, frankenMap
  };


  // Sidebar shimmer: `loading` only flips true in the post-switch effect,
  // so for one frame after a mode change the sidebar would flash the new
  // view's content before the shimmer starts (visible on back-to-overview,
  // where the legend is static and the card is auto-height). Data that
  // doesn't match the mode yet counts as loading too.
  const sidebarLoading = loading
    || (mode.kind === 'region' && data?.region !== mode.name)
    || (mode.kind === 'regions' && !data?.overview);

  // The right-hand column (region sidebar) — rendered in the normal layout,
  // or routed into the fly-through overlay while it is open so the user can
  // browse without leaving the 3D view. Back-to-regions is disabled there.
  // First-paint veil: the initial load assembles in visible stages (GL
  // basemap tile by tile, cached-then-fresh polygons, labels) — a solid
  // cover in the map background color fades out ONCE, when the basemap
  // reports idle and the overview data has painted. Hard 2.5s backstop so
  // the veil can never stick (Carto fallback, WebGL off, slow network).
  const [basemapIdle, setBasemapIdle] = useState(false);
  const [veilReady, setVeilReady] = useState(false);
  const onBasemapReady = useCallback(() => setBasemapIdle(true), []);
  useEffect(() => {
    if (veilReady || !(basemapIdle && data)) return;
    // two frames so the polygons actually paint beneath the veil first
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setVeilReady(true)); });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
  }, [basemapIdle, data, veilReady]);
  useEffect(() => {
    const t = setTimeout(() => setVeilReady(true), 2500);
    return () => clearTimeout(t);
  }, []);
  // The veil itself is static DOM in index.html (it animates from the
  // first HTML paint, before this bundle even loads). Once the map is
  // ready, fade it at the NEXT clink-cycle boundary so the cheers never
  // cuts off mid-motion; the timeout covers paused animations
  // (prefers-reduced-motion), where animationiteration never fires.
  useEffect(() => {
    if (!veilReady) return;
    const el = document.getElementById('load-veil');
    if (!el) return;
    let done = false;
    const fade = () => {
      if (done) return;
      done = true;
      el.classList.add('load-veil--fading');
      setTimeout(() => el.remove(), 500);
    };
    el.addEventListener('animationiteration', fade);
    const t = setTimeout(fade, 2400);
    return () => { el.removeEventListener('animationiteration', fade); clearTimeout(t); };
  }, [veilReady]);

  // Study Mode is signed-in only: bounce once auth resolves signed-out.
  useEffect(() => {
    if (studyMode && !authLoading && !user) setStudyMode(false);
  }, [studyMode, authLoading, user]);
  const enterStudy = () => {
    setProducersLayer(false);
    setGeoLayer(false);
    setStudyMode(true);
  };

  // trip mode always plays out in its region: entering (button or deep
  // link) drives the normal region navigation, leaving returns home
  useEffect(() => {
    if (tripMode) selectRegion(ATLAS.tripPlanner.region);
  }, [tripMode, selectRegion]);
  const enterTrip = () => {
    setProducersLayer(false);
    setGeoLayer(false);
    setTripMode(true);
  };
  const exitTrip = () => {
    setTripMode(false);
    setTripPlan(null);
    switchMode({ kind: 'regions' });
  };

  const sidebarEl = (
    <Sidebar
      regionBackDisabled={flythrough}
      onExitFlythrough={() => setFlythrough(null)}
      user={user}
      signIn={signIn}
      signOut={signOut}
      showVillages={showVillages}
      toggleVillages={() => { markToggleOrigin('villages'); setVillageToggle(t => t === 'off' ? 'pinned' : 'off'); }}
      showProducers={showProducers}
      toggleProducers={() => setProducerToggle(t => t === 'off' ? 'pinned' : 'off')}
      showLabels={showLabels}
      toggleLabels={() => { markToggleOrigin('labels'); setShowLabels(v => !v); }}
      mapZoom={mapZoom}
      showAllVineyards={showAllVineyards}
      toggleAllVineyards={() => { markToggleOrigin('filter'); setShowAllVineyards(v => !v); }}
      hoveredRegion={hoveredRegion}
      onHoverRegion={setHoveredRegion}
      onHoverVillage={setHoveredVillage}
      onHoverVineyard={setHoveredVineyardId}
      producersLayer={producersLayer}
      keyProducers={keyProducers}
      onSelectKeyProducer={selectKeyProducer}
      onHoverKeyProducer={setHoveredKeyProducer}
      onProducerFilter={setProducerFilterIds}
      loading={sidebarLoading}
      lassoArmed={lassoArmed}
      setLassoArmed={setLassoArmed}
      mode={mode}
      onSelectRegion={selectRegion}
      onSetMode={switchMode}
      focusBereich={focusBereich}
      onFocusBereich={setFocusBereich}
      onSelectVineyard={(id) => { setZoomToSelection(true); onSelectVineyard(id); }}
      onSelectVillage={(v) => { setVillageToggle(t => t === 'off' ? 'transient' : t); setZoomToSelection(true); onSelectVillage(v); }}
      onSelectProducer={(p) => { setProducerToggle(t => t === 'off' ? 'transient' : t); setZoomToSelection(true); setSelection({ type: 'producer', producer: p }); }}
      villageEditMode={villageEditMode}
      setVillageEditMode={(v) => { if (typeof v === 'function') { setVillageEditMode(prev => { const next = v(prev); if (next) setVillageToggle(t => t === 'off' ? 'pinned' : t); return next; }); } else { if (v) setVillageToggle(t => t === 'off' ? 'pinned' : t); setVillageEditMode(v); } }}
      labelEditMode={labelEditMode}
      regionLabelEditMode={regionLabelEditMode}
      setRegionLabelEditMode={setRegionLabelEditMode}
      geoLayer={geoLayer}
      geoLabelEditMode={geoLabelEditMode}
      setGeoLabelEditMode={setGeoLabelEditMode}
      riverLabelEditMode={riverLabelEditMode}
      setRiverLabelEditMode={setRiverLabelEditMode}
      mtnIconEditMode={mtnIconEditMode}
      setMtnIconEditMode={setMtnIconEditMode}
      setLabelEditMode={setLabelEditMode}
      helpOpen={helpOpen}
      setHelpOpen={setHelpOpen}
      onSearchResult={handleSearchResult}
      onStartDraw={startDraw}
    />
  );
  return (
    <div className={`app app--sheet-${sheetPos} ${!sidebarOpen ? 'app--sidebar-collapsed' : ''} ${mode.kind === 'regions' ? 'app--sidebar-regions' : ''} ${producersLayer && mode.kind === 'regions' ? 'app--producers' : ''} ${user ? 'app--signed-in' : ''} ${studyMode ? 'app--study' : ''}`}>
      <div className="map-wrap">
        {flythrough && (
          <Suspense fallback={null}>
            <FlyThrough
              key={flythrough.key}
              tour={flythrough}
              start={flythrough.start}
              canEdit={!!user}
              onClose={() => setFlythrough(null)}
              onSelectVineyard={(id) => {
                setZoomToSelection(false);
                setSelection({ type: 'single', id });
              }}
              panel={selectionPanel
                ? <Suspense fallback={null}>{selectionPanel}</Suspense>
                : sidebarEl}
            />
          </Suspense>
        )}
        {tripMode && (
          <div className="modes modes--back">
            {/* with a producer (or any) panel open, back returns to the
                planner — trip plan and form state survive (the planner
                stays mounted, only hidden); a second back leaves trip mode */}
            <button className="mode-btn mode-btn--back" onClick={() => { if (selection.type !== 'none') closeSelection(); else exitTrip(); }} title="Back" aria-label="Back">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
          </div>
        )}
        {!studyMode && !tripMode && <ModeSwitcher mode={mode} onBack={goBack} backLabel={backLabel} producersLayer={producersLayer} geoLayer={geoLayer} overlaySelection={selection.type === 'geo'} showOutOfScope={showOutOfScope} toggleOutOfScope={() => setShowOutOfScope(v => !v)} satellite={satellite} terrain={terrain} frankenMap={frankenMap} lagenAvailable={lagenAvailable} climateLayer={climateLayer} flurbLayer={flurbLayer} flurbAvailable={flurbAvailable} rlpLagenLayer={rlpLagenLayer} rlpLagenAvailable={rlpLagenAvailable} precipAvailable={!!precipRegion} onSelectView={selectView} onAbout={() => setHelpOpen(true)} onStudy={ATLAS.features.study && user && mode.kind === 'regions' ? enterStudy : undefined} onTrip={ATLAS.tripPlanner && mode.kind === 'region' && !tripMode && (mode.name === ATLAS.tripPlanner.region || (ATLAS.nestedRegions?.[ATLAS.tripPlanner.region] ?? []).includes(mode.name)) ? enterTrip : undefined} searchOpen={searchOpen} onOpenSearch={openSearch} onCloseSearch={closeSearch} onSearchResult={handleSearchResult} user={user} onRefresh={refreshData} />}
        <MapView
          mode={mode}
          studyCentered={studyMode}
          hideVineyards={tripMode}
          tripPlan={tripMode ? tripPlan : null}
          onBasemapReady={onBasemapReady}
          data={data}
          dataVersion={dataVersion}
          loading={loading}
          selectedId={selectedId}
          selectedIds={selectedIds}
          onSelectVineyard={onSelectVineyardFromMap}
          onSelectRegion={selectRegion}
          onLassoFinished={onLassoFinished}
          lassoArmed={lassoArmed}
          focusBereich={focusBereich}
          onFocusBereich={setFocusBereich}
          onSelectVillage={onSelectVillageFromMap}
          onSelectProducer={onSelectProducerFromMap}
          producersLayer={producersLayer}
          keyProducers={keyProducers}
          onSelectKeyProducer={selectKeyProducer}
          hoveredKeyProducerId={hoveredKeyProducer}
          producerFilterIds={producerFilterIds}
          geoLayer={geoLayer}
          onSelectGeoFeature={onSelectGeoFeature}
          overviewFitSignal={overviewFitSignal}
          selectedGeoFeature={selection.type === 'geo' ? { kind: selection.kind, name: selection.name } : null}
          villageEditMode={villageEditMode}
          showVillages={showVillages}
          showProducers={showProducers}
          zoomToSelection={zoomToSelection}
          selectedVillage={selection.type === 'village' ? selection.village : null}
          selectedProducer={selection.type === 'producer' ? selection.producer : null}
          labelEditMode={labelEditMode}
          regionLabelEditMode={regionLabelEditMode && !!user}
          geoLabelEditMode={geoLabelEditMode && !!user}
          riverLabelEditMode={riverLabelEditMode && !!user}
          mtnIconEditMode={mtnIconEditMode && !!user}
          geomEdit={geomEdit}
          geomEditRingValid={geomEditRingValid}
          splitSession={splitSession}
          onSplitAddPoint={splitAddPoint}
          onSplitUndoPoint={splitUndoPoint}
          onSplitFinishLine={splitFinishLine}
          onGeomVertexMoved={moveGeomVertex}
          onGeomVertexInserted={insertGeomVertex}
          onGeomVerticesDeleted={deleteGeomVertices}
          showLabels={showLabels}
          showAllVineyards={showAllVineyards}
          outOfScopeData={outOfScopeData}
          draw={drawApi}
          flurbActive={flurbLayer}
          loadError={error}
          onRetry={refetch}
          hoveredRegion={hoveredRegion}
          onHoverRegion={setHoveredRegion}
          hoveredVillage={hoveredVillage}
          hoveredVineyardId={hoveredVineyardId}
          matchHighlightIds={matchHighlightIds}
          flyTours={flyTours}
          onStartFlythrough={setFlythrough}
          rlpLagenActive={rlpLagenLayer}
          rlpLagenFC={rlpLagenFC}
          addedLageIds={addedLageIds}
          selectedLageId={selection.type === 'lage' ? selection.feature.properties.id : null}
          onSelectLage={onSelectLage}
          flurbProcedures={flurb.proceduresFC}
          flurbOverlaps={flurb.overlapsFC}
          selectedFlurbOid={selection.type === 'flurb' ? selection.oid : null}
          onSelectFlurb={onSelectFlurb}
          mapZoom={mapZoom}
          satellite={satellite}
          climateLayer={climateLayer}
          terrain={terrain}
          tempRegion={tempRegion}
          precipRegion={precipRegion}
          frankenMap={frankenMap}
          lagenBounds={lagenBounds}
          onLagenBoundsChange={onLagenBoundsChange}
          lagenAdjustable={!!user}
          onZoomChange={setMapZoom}
          skipFitToData={hasPendingSearch}
          flyToPoint={flyToPoint}
          onFlyToComplete={() => setFlyToPoint(null)}
          previewFeature={previewFeature}
        />
        {error && <div className="map-error"><WarnIcon size={13} style={{ verticalAlign: '-2px', marginRight: '0.35em' }} />{error}</div>}
      </div>
      <div className={`sidebar-wrap ${sidebarOpen ? '' : 'sidebar-hidden'}`} ref={sheetRef}>
        <div
          className="sheet-handle"
          onPointerDown={onSheetDown}
          onPointerMove={onSheetMove}
          onPointerUp={onSheetUp}
          onPointerCancel={onSheetUp}
        >
          <span className="sheet-handle__bar" />
        </div>
        <div style={(selectionPanel || drawActive || flythrough) ? { display: 'none' } : { display: 'contents' }}>
          {!flythrough && (tripMode ? <TripPlanner plannedRoute={tripPlan} onPlanChange={setTripPlan} /> : sidebarEl)}
        </div>
        {drawActive && (
          <Suspense fallback={<DetailSkeleton name="Draw Vineyards" />}>
            <DrawPanel draw={drawApi} region={mode.kind === 'region' ? mode.name : ''} />
          </Suspense>
        )}
        {!drawActive && !flythrough && selectionPanel && (
          <Suspense fallback={<DetailSkeleton name={
            selection.type === 'village' ? selection.village.name
              : selection.type === 'producer' ? selection.producer.name
              : selection.type === 'single' && searchHint?.id === selection.id ? searchHint.name
              : null
          } />}>
            {selectionPanel}
          </Suspense>
        )}
      </div>
      {!sidebarOpen && mode.kind !== 'regions' && !isMobile() && (() => {
        let vineyardName = null;
        if (selection.type === 'single') {
          if (data?.features) {
            const f = data.features.find(ft => ft.properties.id === selection.id);
            if (f) vineyardName = f.properties.canonical_name_de || f.properties.einzellage || 'Unnamed vineyard';
          } else {
            vineyardName = 'Unnamed vineyard';
          }
        }
        const regionName = mode.kind === 'region' ? mode.name : '';
        const bereiche = regionName ? bereicheFor(regionName) : [];
        const bereichName = focusBereich || (bereiche.length === 1 ? bereiche[0] : null);
        return (
          <aside className="sidebar-collapsed" onClick={openSidebar}>
            <button className="sidebar-collapse-btn sidebar-collapse-btn--closed" onClick={e => { e.stopPropagation(); openSidebar(); }} title="Expand sidebar">
              <svg width="10" height="16" viewBox="0 0 10 16" className="sidebar-collapse-icon sidebar-collapse-icon--closed">
                <polyline points="8,1 2,8 8,15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="sidebar-collapsed__label">
              <strong>{regionName}</strong>
              {bereichName ? <> | <strong>{bereichName}</strong></> : ''}
              {vineyardName ? ` | ${vineyardName}` : ''}
              {selection.type === 'village' ? ` | ${selection.village.name}` : ''}
              {selection.type === 'producer' ? ` | ${selection.producer.name}` : ''}
            </span>
          </aside>
        );
      })()}
      {sidebarOpen && mode.kind !== 'regions' && !isMobile() && (
        <button className="sidebar-collapse-btn" onClick={() => setSidebarOpen(false)} title="Collapse sidebar">
          <svg width="10" height="16" viewBox="0 0 10 16" className="sidebar-collapse-icon">
            <polyline points="8,1 2,8 8,15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {studyMode && user && (
        <Suspense fallback={null}>
          <StudyMode onExit={() => setStudyMode(false)} />
        </Suspense>
      )}
      <Analytics />
    </div>
  );
}
